import React, { useState, useEffect, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import ChevronIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import clsx from "clsx";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import useMenuStyles from "../../components/menuStyles";
import DiffView from "../../components/DiffView";

// Aba "Versões" do agente = o PASSADO (handoff §4.5). Cada linha é uma versão
// publicada, descrita em texto humano; clicar expande o comparativo com a versão
// anterior. Sugestões novas da IA são analisadas/aprovadas na aba Correções.

// como cada seção do snapshot aparece numa frase ("Alterou as instruções e a voz")
const HUMAN = {
  "cfg:persona": "as instruções",
  "cfg:greeting": "a saudação",
  "app:notes": "a base de conhecimento",
  "cfg:chatModel": "o modelo de IA (texto)",
  "cfg:elevenLlm": "o modelo de IA (voz)",
  "cfg:provider": "o provedor de voz",
  "cfg:voice": "a voz",
  "cfg:toolIds": "as ferramentas",
  "cfg:systemTools": "as ferramentas do sistema",
  "cfg:outputs": "as saídas",
  "cfg:transferDestinations": "as transferências",
  "cfg:humanTransferTo": "o destino da transferência humana",
  "cfg:transferToHuman": "a transferência para humano",
  "cfg:humanize": "a humanização",
  "app:name": "o nome",
  "app:description": "a descrição",
};

const joinPt = parts => (parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} e ${parts[parts.length - 1]}`);

const changedText = diff => {
  const parts = (diff || []).map(d => HUMAN[d.key] || d.label);
  return parts.length ? `Alterou ${joinPt(parts)}` : "Sem mudanças em relação à anterior";
};

// título da linha: a nota (ex.: resumo da sugestão aprovada) ou uma frase gerada
const titleOf = v => {
  if (v.revertedFrom) return `Voltou para a v${v.revertedFrom}`;
  if (v.number === 1) return "Versão inicial do agente";
  if (v.note && v.note !== "proposta aprovada") return v.note;
  return changedText(v.diff);
};

const useStyles = makeStyles(theme => {
  const a = theme.app || {};
  return {
    wrap: { padding: "20px 0" },
    desc: { fontSize: 13.5, color: a.textTertiary || "#929AA4", margin: "0 0 16px", lineHeight: 1.5 },
    empty: { fontSize: 14, color: a.textSecondary || "#5B6470", padding: "10px 0" },

    list: { background: "#fff", border: `1px solid ${a.border || "#E6E9ED"}`, borderRadius: 14, overflow: "hidden" },
    item: { borderBottom: `1px solid ${a.lineSoft || "#F1F4F2"}`, "&:last-child": { borderBottom: "none" } },
    row: { display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", cursor: "pointer", "&:hover": { background: a.hover || "#F7F8FA" } },
    vnum: { width: 34, flexShrink: 0, fontSize: 13, fontWeight: 800, color: "#0b7285", fontFamily: a.mono || "monospace" },
    main: { flex: 1, minWidth: 0 },
    title: { fontSize: 14, color: a.ink || "#2c3036", fontWeight: 600, lineHeight: 1.4 },
    sub: { fontSize: 12.5, color: a.textTertiary || "#929AA4", marginTop: 2 },
    tag: { flexShrink: 0, fontSize: 11, fontWeight: 700, borderRadius: 6, padding: "2px 8px", color: "#1E7A49", background: "#DCF5E7" },
    chev: { flexShrink: 0, color: a.textTertiary || "#929AA4", transition: "transform .15s" },
    chevOpen: { transform: "rotate(180deg)" },

    body: { padding: "4px 16px 16px 64px", background: "#FCFCFD" },
    diffSec: { marginTop: 12 },
    diffLabel: { fontSize: 12.5, fontWeight: 700, color: "#5B6470", marginBottom: 6 },
    bodyActions: { display: "flex", justifyContent: "flex-end", marginTop: 12 },
  };
});

const AgentVersoes = ({ agentId }) => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const [versions, setVersions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [openVer, setOpenVer] = useState(null); // nº da versão expandida
  const [busy, setBusy] = useState(null);
  const [revertTo, setRevertTo] = useState(null); // nº da versão a reverter (confirm)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/agents/${agentId}/versions`);
      setVersions(data.versions || []);
      setCurrent(data.current || 0);
    } catch (err) {
      toastError(err);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const doRevert = async () => {
    const n = revertTo;
    setRevertTo(null);
    setBusy("revert");
    try {
      await api.post(`/agents/${agentId}/versions/${n}/revert`);
      toast.success(`Revertido para a v${n} (publicado como versão nova).`);
      load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.desc}>
        Histórico do agente: todo salvamento e toda sugestão aprovada viram uma linha aqui. Clique para ver o que
        mudou em relação à anterior. Reverter traz de volta uma versão antiga (como versão nova — nada é apagado).
      </div>

      {versions.length === 0 ? (
        <div className={classes.empty}>Nenhuma versão ainda.</div>
      ) : (
        <div className={classes.list}>
          {versions.map(v => {
            const isOpen = openVer === v.number;
            const title = titleOf(v);
            const changed = changedText(v.diff);
            return (
              <div key={v.number} className={classes.item}>
                <div className={classes.row} onClick={() => setOpenVer(isOpen ? null : v.number)}>
                  <span className={classes.vnum}>v{v.number}</span>
                  <div className={classes.main}>
                    <div className={classes.title}>{title}</div>
                    <div className={classes.sub}>
                      {v.number > 1 && title !== changed ? `${changed} · ` : ""}
                      {v.author ? `${v.author} · ` : ""}
                      {v.at ? new Date(v.at).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : ""}
                    </div>
                  </div>
                  {v.number === current ? <span className={classes.tag}>no ar</span> : null}
                  <ChevronIcon className={clsx(classes.chev, isOpen && classes.chevOpen)} />
                </div>
                {isOpen && (
                  <div className={classes.body}>
                    {(v.diff || []).length === 0 ? (
                      <div className={classes.sub}>Sem mudanças em relação à anterior.</div>
                    ) : (
                      v.diff.map(d => (
                        <div key={d.key} className={classes.diffSec}>
                          <div className={classes.diffLabel}>{d.label}</div>
                          <DiffView before={d.before} after={d.after} />
                        </div>
                      ))
                    )}
                    {v.number !== current && (
                      <div className={classes.bodyActions}>
                        <Button size="small" disabled={busy === "revert"} onClick={() => setRevertTo(v.number)}>Reverter para esta versão</Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Reverter: confirmação (sem popup nativo) */}
      <Dialog open={revertTo != null} onClose={() => setRevertTo(null)} maxWidth="xs" fullWidth PaperProps={{ className: menu.dialogPaper }}>
        <DialogTitle>Reverter para a v{revertTo}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            O conteúdo da v{revertTo} será publicado como uma <b>versão nova</b>. Nada do histórico é apagado.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertTo(null)}>Cancelar</Button>
          <Button variant="contained" color="primary" disableElevation disabled={busy === "revert"} onClick={doRevert}>Reverter</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AgentVersoes;
